/**
 * ChartSpec — renderer-agnostic chart contract for OIKRA.
 * See docs/design-v3/charts.md
 */

/** Preferred max overlapping series before suggesting small multiples. */
export const CHART_SERIES_SOFT_LIMIT = 5;

/** Hard cap aligned with --ds-chart-1…8 / chartColors.js. */
export const CHART_SERIES_HARD_LIMIT = 8;

export const CHART_DENSITIES = Object.freeze(['analysis', 'editorial']);
export const CHART_MARKS = Object.freeze(['line', 'area', 'bar']);
export const CHART_TRANSFORMS = Object.freeze(['none', 'yoy', 'mom', 'index100']);
export const CHART_RANGE_PRESETS = Object.freeze(['1y', '5y', '10y', 'all']);
export const CHART_DERIVATION = Object.freeze([
  'observed',
  'derived_oikra',
  'interpretation',
]);

/**
 * @typedef {'analysis'|'editorial'} ChartDensity
 * @typedef {'line'|'area'|'bar'} ChartMark
 * @typedef {'none'|'yoy'|'mom'|'index100'} ChartTransform
 * @typedef {'1y'|'5y'|'10y'|'all'} ChartRangePreset
 * @typedef {'observed'|'derived_oikra'|'interpretation'} ChartDerivationStatus
 *
 * @typedef {Object} ChartPoint
 * @property {string} t ISO date or period key (YYYY-MM, YYYY-Qn, …)
 * @property {number|null} v
 *
 * @typedef {Object} ChartSeries
 * @property {string} id
 * @property {string} label Human-readable name
 * @property {string} [unit]
 * @property {ChartPoint[]} points
 * @property {0|1} [axis] Primary (0) or secondary (1) axis
 * @property {ChartMark} [mark] Per-series mark; falls back to ChartSpec.mark
 * @property {boolean} [stack] When true with mark bar/area, stack with other stacked series
 * @property {boolean} [hidden]
 * @property {string} [color] Optional override (hex); else palette token
 *
 * @typedef {Object} ChartAnnotation
 * @property {string} id
 * @property {string} t Anchor on the time axis
 * @property {string} label
 * @property {string} [description]
 *
 * @typedef {Object} ChartProvenance
 * @property {string} [source]
 * @property {string} [observationPeriod] UI: series-end / último dato (chart footer only; tooltip uses the hovered point)
 * @property {string} [publishedAt] UI: Publicado
 * @property {ChartDerivationStatus} [derivationStatus]
 * @property {string} [revisionId] Extended layer only
 * @property {string} [methodology]
 * @property {string} [coverageLabel] e.g. "2010–2026 · mensual"
 *
 * @typedef {Object} ChartSpec
 * @property {string} title Finding-oriented title
 * @property {string} [subtitle]
 * @property {ChartDensity} density
 * @property {ChartMark} [mark]
 * @property {ChartSeries[]} series
 * @property {ChartTransform} [transform]
 * @property {ChartRangePreset} [range]
 * @property {ChartAnnotation[]} [annotations]
 * @property {ChartProvenance} [provenance]
 * @property {boolean} [dualAxis]
 * @property {string} [yAxisLabel]
 * @property {string} [yAxisLabelSecondary]
 */

/**
 * @typedef {Object} NormalizedChartSpec
 * @property {ChartSpec} spec
 * @property {string[]} warnings
 * @property {boolean} suggestSmallMultiples
 * @property {boolean} dualAxisWarning
 * @property {number} visibleSeriesCount
 */

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function clampMark(mark) {
  return CHART_MARKS.includes(mark) ? mark : 'line';
}

/** Effective mark for a series (per-series override, else chart default). */
export function resolveSeriesMark(series, chartMark = 'line') {
  if (series?.mark && CHART_MARKS.includes(series.mark)) return series.mark;
  return clampMark(chartMark);
}

/** True when visible series do not all share one mark (needs ComposedChart). */
export function hasMixedMarks(series, chartMark = 'line') {
  const list = asArray(series).filter((s) => s && !s.hidden);
  if (list.length < 2) return false;
  const marks = new Set(list.map((s) => resolveSeriesMark(s, chartMark)));
  return marks.size > 1;
}

/**
 * Y domain for one axis. Bars and areas measure from zero, so they keep Recharts' zero baseline;
 * an axis that only draws lines fits the visible range, or a level that moves 4% draws flat.
 */
export function yAxisDomain(series, chartMark = 'line', axis = 0) {
  const onAxis = asArray(series).filter((s) => s && !s.hidden && (s.axis === 1 ? 1 : 0) === axis);
  if (!onAxis.length) return undefined;
  return onAxis.every((s) => resolveSeriesMark(s, chartMark) === 'line') ? ['auto', 'auto'] : undefined;
}

/** Paint order for ComposedChart: bars under areas under lines. */
export const MARK_PAINT_ORDER = Object.freeze(['bar', 'area', 'line']);

export function sortSeriesForPaint(series, chartMark = 'line') {
  const list = asArray(series).filter((s) => s && !s.hidden);
  return [...list].sort((a, b) => {
    const ia = MARK_PAINT_ORDER.indexOf(resolveSeriesMark(a, chartMark));
    const ib = MARK_PAINT_ORDER.indexOf(resolveSeriesMark(b, chartMark));
    return ia - ib;
  });
}

/** Digits shown after the decimal point; undefined lets the chart pick. */
function clampDecimals(value) {
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 && n <= 4 ? n : undefined;
}

function clampTransform(transform) {
  return CHART_TRANSFORMS.includes(transform) ? transform : 'none';
}

function clampRange(range) {
  return CHART_RANGE_PRESETS.includes(range) ? range : 'all';
}

function clampDensity(density) {
  return CHART_DENSITIES.includes(density) ? density : 'analysis';
}

function normalizePoint(point) {
  if (!point || point.t == null) return null;
  const v = point.v;
  return {
    t: String(point.t),
    v: v == null || v === '' || Number.isNaN(Number(v)) ? null : Number(v),
  };
}

function normalizeSeries(raw, index) {
  if (!raw || !raw.id) return null;
  const points = asArray(raw.points).map(normalizePoint).filter(Boolean);
  const mark = raw.mark && CHART_MARKS.includes(raw.mark) ? raw.mark : undefined;
  return {
    id: String(raw.id),
    label: String(raw.label || raw.id),
    unit: raw.unit ? String(raw.unit) : undefined,
    points,
    axis: raw.axis === 1 ? 1 : 0,
    mark,
    stack: Boolean(raw.stack),
    hidden: Boolean(raw.hidden),
    color: raw.color ? String(raw.color) : undefined,
    _index: index,
  };
}

/**
 * Validate and clamp a ChartSpec for safe rendering.
 * Does not mutate the input.
 * @param {Partial<ChartSpec>|null|undefined} input
 * @returns {NormalizedChartSpec}
 */
export function normalizeChartSpec(input) {
  const warnings = [];
  const raw = input && typeof input === 'object' ? input : {};
  const seriesAll = asArray(raw.series)
    .map(normalizeSeries)
    .filter(Boolean);

  if (seriesAll.length > CHART_SERIES_HARD_LIMIT) {
    warnings.push(`series_clamped_to_${CHART_SERIES_HARD_LIMIT}`);
  }

  const series = seriesAll.slice(0, CHART_SERIES_HARD_LIMIT);
  const visibleSeriesCount = series.filter((s) => !s.hidden).length;
  const suggestSmallMultiples = visibleSeriesCount > CHART_SERIES_SOFT_LIMIT;

  if (suggestSmallMultiples) {
    warnings.push('suggest_small_multiples');
  }

  const units = [...new Set(
    series.filter((s) => !s.hidden && s.unit).map((s) => s.unit),
  )];
  const dualAxis = Boolean(raw.dualAxis);
  let dualAxisWarning = false;

  if (dualAxis && units.length > 2) {
    dualAxisWarning = true;
    warnings.push('dual_axis_requires_at_most_two_units');
  } else if (dualAxis && units.length === 2) {
    dualAxisWarning = true;
    warnings.push('dual_axis_active');
  } else if (!dualAxis && units.length > 2) {
    warnings.push('incompatible_units_prefer_index_or_small_multiples');
  }

  if (!raw.title) {
    warnings.push('missing_title');
  }

  const provenance = raw.provenance && typeof raw.provenance === 'object'
    ? {
        source: raw.provenance.source ? String(raw.provenance.source) : undefined,
        observationPeriod: raw.provenance.observationPeriod
          ? String(raw.provenance.observationPeriod)
          : undefined,
        publishedAt: raw.provenance.publishedAt
          ? String(raw.provenance.publishedAt)
          : undefined,
        derivationStatus: CHART_DERIVATION.includes(raw.provenance.derivationStatus)
          ? raw.provenance.derivationStatus
          : undefined,
        revisionId: raw.provenance.revisionId
          ? String(raw.provenance.revisionId)
          : undefined,
        methodology: raw.provenance.methodology
          ? String(raw.provenance.methodology)
          : undefined,
        coverageLabel: raw.provenance.coverageLabel
          ? String(raw.provenance.coverageLabel)
          : undefined,
      }
    : {};

  if (!provenance.source) warnings.push('missing_source');
  if (!provenance.observationPeriod && !provenance.coverageLabel) {
    warnings.push('missing_period_or_coverage');
  }

  const annotations = asArray(raw.annotations)
    .filter((a) => a && a.id && a.t && a.label)
    .map((a) => ({
      id: String(a.id),
      t: String(a.t),
      label: String(a.label),
      description: a.description ? String(a.description) : undefined,
    }));

  /** @type {ChartSpec} */
  const spec = {
    title: String(raw.title || ''),
    subtitle: raw.subtitle ? String(raw.subtitle) : undefined,
    density: clampDensity(raw.density),
    mark: clampMark(raw.mark),
    series: series.map(({ _index, ...rest }) => rest),
    transform: clampTransform(raw.transform),
    range: clampRange(raw.range),
    annotations,
    provenance,
    dualAxis,
    decimals: clampDecimals(raw.decimals),
    yAxisLabel: raw.yAxisLabel ? String(raw.yAxisLabel) : undefined,
    yAxisLabelSecondary: raw.yAxisLabelSecondary
      ? String(raw.yAxisLabelSecondary)
      : undefined,
  };

  return {
    spec,
    warnings,
    suggestSmallMultiples,
    dualAxisWarning,
    visibleSeriesCount,
  };
}

/**
 * Build Recharts-friendly rows from series points (outer join on `t`).
 * @param {ChartSeries[]} series
 * @returns {Record<string, string|number|null>[]}
 */
export function seriesToRechartsData(series) {
  const list = asArray(series).filter((s) => s && !s.hidden);
  const times = new Set();
  list.forEach((s) => {
    asArray(s.points).forEach((p) => {
      if (p?.t != null) times.add(String(p.t));
    });
  });

  const sorted = [...times].sort();
  return sorted.map((t) => {
    const row = { t };
    list.forEach((s) => {
      const point = asArray(s.points).find((p) => String(p.t) === t);
      row[s.id] = point && point.v != null ? point.v : null;
    });
    return row;
  });
}

/**
 * Client-side range filter by trailing calendar years from the last point.
 * @param {Record<string, string|number|null>[]} rows
 * @param {ChartRangePreset} range
 */
export function applyRangePreset(rows, range) {
  const data = asArray(rows);
  if (!data.length || range === 'all') return data;

  const years = range === '1y' ? 1 : range === '5y' ? 5 : range === '10y' ? 10 : null;
  if (!years) return data;

  const last = data[data.length - 1]?.t;
  const lastDate = parseChartTime(last);
  if (!lastDate) return data;

  const cutoff = new Date(lastDate);
  cutoff.setFullYear(cutoff.getFullYear() - years);
  return data.filter((row) => {
    const d = parseChartTime(row.t);
    return d && d >= cutoff;
  });
}

/**
 * Client-side YoY / MoM / index-100 on joined Recharts rows.
 * YoY uses a 12-row lag (monthly-ish). Apply on the full series *before*
 * {@link applyRangePreset} so the lag has lookback outside the display window.
 *
 * @param {Record<string, string|number|null>[]} rows
 * @param {{ id: string }[]} series
 * @param {ChartTransform} transform
 */
export function applyChartTransform(rows, series, transform) {
  const data = asArray(rows);
  const list = asArray(series);
  if (!transform || transform === 'none' || !data.length || !list.length) return data;

  if (transform === 'index100') {
    const bases = {};
    list.forEach((s) => {
      const first = data.find((row) => row[s.id] != null && Number.isFinite(row[s.id]));
      bases[s.id] = first ? Number(first[s.id]) : null;
    });
    return data.map((row) => {
      const next = { ...row };
      list.forEach((s) => {
        const base = bases[s.id];
        const value = row[s.id];
        next[s.id] =
          base && value != null && Number.isFinite(value) ? (Number(value) / base) * 100 : null;
      });
      return next;
    });
  }

  const lag = transform === 'yoy' ? 12 : 1;
  return data.map((row, index) => {
    const next = { ...row };
    list.forEach((s) => {
      const current = row[s.id];
      const previous = data[index - lag]?.[s.id];
      if (
        current == null ||
        previous == null ||
        !Number.isFinite(Number(current)) ||
        !Number.isFinite(Number(previous)) ||
        Number(previous) === 0
      ) {
        next[s.id] = null;
      } else {
        next[s.id] = (Number(current) / Number(previous) - 1) * 100;
      }
    });
    return next;
  });
}

/** Drop leading rows where every series value is null (typical YoY warm-up). */
export function trimLeadingNullChartRows(rows, series) {
  const data = asArray(rows);
  const list = asArray(series);
  if (!data.length || !list.length) return data;
  const hasValue = (row) => list.some((s) => row[s.id] != null && Number.isFinite(Number(row[s.id])));
  const start = data.findIndex(hasValue);
  return start === -1 ? [] : data.slice(start);
}

export function hasPlottableChartValues(rows, series) {
  const data = asArray(rows);
  const list = asArray(series);
  return data.some((row) =>
    list.some((s) => row[s.id] != null && Number.isFinite(Number(row[s.id]))),
  );
}

/**
 * Short label for a time-axis tick: "T2 26" for a quarter, "may 24" for a month, "12 sep 25" for a day.
 * Anything else is returned as it came.
 */
export function axisPeriodLabel(raw, locale = 'es-AR') {
  if (raw == null) return '';
  const s = String(raw).trim();
  const yq = /^(\d{4})-Q([1-4])$/i.exec(s);
  if (yq) return `${String(locale).startsWith('en') ? 'Q' : 'T'}${yq[2]} ${yq[1].slice(2)}`;
  const isMonth = /^\d{4}-\d{2}$/.test(s);
  const isDay = /^\d{4}-\d{2}-\d{2}/.test(s);
  const date = parseChartTime(s);
  if ((!isMonth && !isDay) || !date) return s;
  const parts = isMonth ? { month: 'short', year: '2-digit' } : { day: 'numeric', month: 'short', year: '2-digit' };
  return date.toLocaleDateString(locale, parts).replace(/\./g, '').replace(/ de /g, ' ');
}

/**
 * Label for a value-axis tick. From ten thousand up it goes compact ("130 M", "1,6 mil"), or nominal GDP in pesos
 * prints nine digits in a 48px gutter; below that, the chart's decimals when it sets them.
 */
export function axisValueLabel(value, locale = 'es-AR', decimals = null) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return value;
  if (Math.abs(value) >= 10000) {
    return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
  }
  if (decimals == null) return value.toLocaleString(locale);
  return value.toLocaleString(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/** @param {unknown} raw */
export function parseChartTime(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (ymd) return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  const ym = /^(\d{4})-(\d{2})$/.exec(s);
  if (ym) return new Date(Number(ym[1]), Number(ym[2]) - 1, 1);
  const yq = /^(\d{4})-Q([1-4])$/i.exec(s);
  if (yq) return new Date(Number(yq[1]), (Number(yq[2]) - 1) * 3, 1);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}
